<template>
    <div class="space-y-8">
        <frosted-bar :breadcrumbs="breadcrumbs">
            <div class="flex items-center justify-between space-x-4">
                <h1>{{ _mft('integrations:page.heading') }}</h1>
            </div>
        </frosted-bar>

        <header class="max-w-3xl">
            <p class="mt-2 text-sm text-gray-600">
                {{ _mft('integrations:page.intro') }}
            </p>
        </header>

        <div v-if="errorMessage" class="p-4 text-sm text-red-700 border border-red-200 rounded-lg bg-red-50">
            {{ errorMessage }}
        </div>

        <div v-if="isLoading" class="grid gap-6 md:grid-cols-2">
            <div v-for="index in 2" :key="index" class="p-6 bg-white border border-gray-200 shadow-sm rounded-2xl">
                <div class="w-1/2 h-4 bg-gray-200 rounded animate-pulse"></div>
                <div class="h-3 mt-4 bg-gray-100 rounded animate-pulse"></div>
                <div class="h-3 mt-2 bg-gray-100 rounded animate-pulse"></div>
                <div class="flex items-center justify-end mt-6">
                    <div class="w-24 h-8 bg-gray-200 rounded-full animate-pulse"></div>
                </div>
            </div>
        </div>

        <div v-else class="space-y-12">
            <section
                v-for="section in sections"
                :key="section.key"
                class="space-y-6"
            >
                <header class="space-y-2">
                    <h2 class="text-xl font-semibold text-gray-900">
                        {{ translateKey(section.title_key) }}
                    </h2>
                    <p v-if="section.description_key" class="text-sm text-gray-600">
                        {{ translateKey(section.description_key) }}
                    </p>
                </header>

                <div class="grid gap-6 md:grid-cols-2">
                    <article
                        v-for="integration in section.items"
                        :key="integration.key"
                        class="flex flex-col justify-between h-full p-6 space-y-6 bg-white border border-gray-200 shadow-sm rounded-2xl"
                    >
                        <div class="space-y-4">
                            <div class="flex items-center gap-4">
                                <div v-if="integration.logo" class="flex-shrink-0">
                                    <div
                                        class="flex items-center justify-center w-12 h-12 border border-white shadow-sm rounded-2xl"
                                        :style="logoStyle(integration.logo)"
                                    >
                                        <img
                                            v-if="integration.logo.src"
                                            class="object-contain w-8 h-8 rounded-lg"
                                            :src="integration.logo.src"
                                            :alt="integration.logo.alt || integration.display_name"
                                        >
                                        <span
                                            v-else-if="integration.logo.label"
                                            class="text-sm font-semibold text-gray-700"
                                        >
                                            {{ integration.logo.label }}
                                        </span>
                                    </div>
                                </div>
                                <div class="flex-1 min-w-0">
                                    <h3 class="text-lg font-semibold text-gray-900">
                                        {{ integration.display_name }}
                                    </h3>
                                </div>
                                <span
                                    v-if="typeof integration.connected === 'boolean'"
                                    class="inline-flex items-center gap-2 px-3 py-1 ml-auto text-xs font-medium rounded-full whitespace-nowrap"
                                    :class="integration.connected ? 'bg-green-50 text-green-700 ring-1 ring-green-200' : 'bg-gray-100 text-gray-600'"
                                >
                                    <span
                                        class="inline-flex w-2 h-2 rounded-full"
                                        :class="integration.connected ? 'bg-green-500' : 'bg-gray-400'"
                                    ></span>
                                    <span>
                                        {{
                                            integration.connected
                                                ? _mft('integrations:status.connected')
                                                : _mft('integrations:status.disconnected')
                                        }}
                                    </span>
                                </span>
                                <span
                                    v-else-if="integration.status_label_key"
                                    class="inline-flex items-center gap-2 px-3 py-1 ml-auto text-xs font-medium text-indigo-700 rounded-full bg-indigo-50 whitespace-nowrap"
                                >
                                    {{ translateKey(integration.status_label_key) }}
                                </span>
                            </div>

                            <p v-if="integration.description_key" class="mt-1 text-sm text-gray-600">
                                {{ translateKey(integration.description_key) }}
                            </p>

                            <dl v-if="integration.connected" class="space-y-2 text-xs text-gray-600">
                                <div v-if="integration.connected_at" class="flex items-center gap-2">
                                    <span class="font-medium text-gray-700">
                                        {{ _mft('integrations:status.connectedOn') }}
                                    </span>
                                    <span>{{ formatTimestamp(integration.connected_at) }}</span>
                                </div>
                                <div v-if="integration.last_sync_at" class="flex items-center gap-2">
                                    <span class="font-medium text-gray-700">
                                        {{ _mft('integrations:status.lastSyncAt') }}
                                    </span>
                                    <span>{{ formatTimestamp(integration.last_sync_at) }}</span>
                                </div>
                                <div v-if="integration.metadata?.scopes?.length">
                                    <span class="font-medium text-gray-700">
                                        {{ _mft('integrations:status.scopes') }}
                                    </span>
                                    <div class="flex flex-wrap gap-2 mt-1">
                                        <span
                                            v-for="scope in integration.metadata.scopes"
                                            :key="scope"
                                            class="inline-flex items-center px-2 py-0.5 text-xs font-medium text-indigo-700 bg-indigo-50 rounded-md"
                                        >
                                            {{ scope }}
                                        </span>
                                    </div>
                                </div>
                            </dl>
                        </div>

                        <div class="flex flex-wrap items-center justify-end gap-3 pt-2">
                            <button-base
                                v-if="integration.connected && integration.actions?.manual_sync_route"
                                size="sm"
                                style-type="secondary"
                                classes="px-4"
                                :disabled="isSyncing(integration.key) || !canTriggerSync(integration)"
                                @click="runManualSync(integration)"
                            >
                                <template v-if="isSyncing(integration.key)">
                                    {{ _mft('integrations:actions.syncing') }}
                                </template>
                                <template v-else>
                                    {{ _mft('integrations:actions.syncNow') }}
                                </template>
                            </button-base>

                            <button-base
                                v-if="integration.connected && integration.actions?.disconnect_route"
                                size="sm"
                                style-type="transparent"
                                classes="border border-gray-200 text-gray-700 hover:bg-gray-100"
                                :disabled="isDisconnecting(integration.key)"
                                @click="disconnectIntegration(integration)"
                            >
                                <template v-if="isDisconnecting(integration.key)">
                                    {{ _mft('integrations:actions.disconnecting') }}
                                </template>
                                <template v-else>
                                    {{ _mft('integrations:actions.disconnect') }}
                                </template>
                            </button-base>

                            <template v-if="integration.actions">
                                <button-base
                                    v-if="integration.actions.connect_route"
                                    size="sm"
                                    style-type="primary"
                                    classes="px-4"
                                    @click="connectIntegration(integration)"
                                >
                                    {{
                                        integration.connected
                                            ? _mft('integrations:actions.reconnect')
                                            : _mft('integrations:actions.connect')
                                    }}
                                </button-base>
                            <button-base
                                v-else-if="integration.actions.manage_route"
                                size="sm"
                                style-type="primary"
                                classes="px-4"
                                @click="navigateTo(integration.actions.manage_route)"
                            >
                                {{
                                    translateKey(
                                        integration.actions.manage_label_key || 'integrations:actions.manage_calendar'
                                    )
                                }}
                            </button-base>
                            </template>
                        </div>
                    </article>
                </div>
            </section>
        </div>

        <p v-if="!isLoading && sections.length === 0" class="text-sm text-gray-600">
            {{ _mft('integrations:empty') }}
        </p>
    </div>
</template>

<script setup>
import { computed, onMounted, ref, watch } from 'vue';
import {settingsBreadcrumbsForKey} from './breadcrumbs';

const props = defineProps({
    initialSections: {
        type: Array,
        default: () => [],
    },
});

const sections = ref([]);
const isLoading = ref(false);
const errorMessage = ref('');
const disconnectingKey = ref(null);
const syncingKey = ref(null);
const breadcrumbs = computed(() => settingsBreadcrumbsForKey('navigation:backOffice.sidebar.myCompany.settings.integrations'));

watch(
    () => props.initialSections,
    (value) => {
        if (Array.isArray(value) && value.length > 0) {
            sections.value = value;
        }
    },
    { immediate: true }
);

const fetchSections = async () => {
    isLoading.value = true;
    errorMessage.value = '';

    try {
        const { data } = await axios.get(route('bo.company.integrations.index'));
        if (Array.isArray(data.sections)) {
            sections.value = data.sections;
        } else if (Array.isArray(data.integrations)) {
            sections.value = [
                {
                    key: 'default',
                    title_key: '',
                    description_key: '',
                    items: data.integrations,
                },
            ];
        }
    } catch (error) {
        console.error(error);
        if (sections.value.length === 0) {
            errorMessage.value = _mft('integrations:errors.loadFailed');
        }
    } finally {
        isLoading.value = false;
    }
};

const connectIntegration = (integration) => {
    const target = integration.actions?.connect_route ?? '/integrations/unimicro/oauth';
    window.location.href = target;
};

const disconnectIntegration = async (integration) => {
    if (!integration.actions?.disconnect_route) {
        return;
    }

    const confirmed = window.confirm(
        _mft('integrations:actions.disconnectConfirm', { name: integration.display_name })
    );
    if (!confirmed) {
        return;
    }

    disconnectingKey.value = integration.key;

    try {
        await axios.delete(integration.actions.disconnect_route);
        await fetchSections();
    } catch (error) {
        console.error(error);
        errorMessage.value = _mft('integrations:errors.disconnectFailed');
    } finally {
        disconnectingKey.value = null;
    }
};

const runManualSync = async (integration) => {
    if (!integration.actions?.manual_sync_route || !canTriggerSync(integration) || syncingKey.value === integration.key) {
        return;
    }

    syncingKey.value = integration.key;
    errorMessage.value = '';

    try {
        await axios.post(integration.actions.manual_sync_route);
        await fetchSections();
    } catch (error) {
        console.error(error);
        const message = error?.response?.data?.error ?? _mft('integrations:errors.syncFailed');
        await fetchSections();
        errorMessage.value = message;
    } finally {
        syncingKey.value = null;
    }
};

const navigateTo = (url) => {
    if (typeof url === 'string' && url !== '') {
        window.location.href = url;
    }
};

const isDisconnecting = computed(() => (key) => disconnectingKey.value === key);
const isSyncing = computed(() => (key) => syncingKey.value === key);

const canTriggerSync = (integration) => integration?.can_trigger_sync !== false;

const formatTimestamp = (value) => {
    if (!value) {
        return '';
    }

    try {
        return new Intl.DateTimeFormat(document.documentElement.lang || 'en', {
            dateStyle: 'medium',
            timeStyle: 'short',
        }).format(new Date(value));
    } catch (error) {
        console.error(error);
        return value;
    }
};

onMounted(fetchSections);

const logoStyle = (logo) => {
    return {
        //backgroundColor: logo?.background ?? '#F5F5F5',
        backgroundColor: '#F5F5F5',
    };
};

const translateKey = (key) => {
    if (!key) {
        return '';
    }

    return _mft(key);
};
</script>
